import { createContext, useContext } from 'react';
export const ZOOM_ATTR_NAME = 'data-tui-html-zoom';
const isBrowser = typeof window !== 'undefined';
const isRootElement = elem => {
  return (elem == null ? void 0 : elem.tagName) === 'BODY' || (elem == null ? void 0 : elem.tagName) === 'HTML';
};

/**
 * Функция для получения числового значения zoom, основаная на getComputedStyle.
 * @param {Element} elem Элемент, для которого требуется получить значение.
 * @returns {number}
 */
export const getElementZoom = elem => {
  if (!elem || !isBrowser) return 1;
  const zoom = Number(window.getComputedStyle(elem).getPropertyValue('zoom'));
  return !Number.isNaN(zoom) && Boolean(zoom) ? zoom : 1;
};

/**
 * Функция для определения суммарного значения zoom, проходя по родителям от заданного элемента.
 * @param {Element} elem Элемент от которого требуется начать проход.
 * @returns {number}
 */
const grabParentsZoom = elem => {
  let currentElem = elem.parentElement;
  let result = 1;
  while (currentElem && !isRootElement(currentElem)) {
    result *= getElementZoom(currentElem);
    currentElem = currentElem.parentElement;
  }
  return result;
};

/**
 * Функция для получения значения zoom из переданных элементов, или проходя по дереву от заданного элемента.
 * Если elems переданы в виде Ref, с Map или Set элементов, то вычисляем их суммарное значение zoom.
 * Если elems переданы как true, то вычисляем значение zoom, проходя по родителям от заданного fromBlock.
 * @param {ZoomElems} elems Элементы для вычисления zoom, или boolean для сбора значений по всему дереву.
 * @param {Element} fromBlock Элемент, от которого требуется совершать проход, если конкретные elems не переданы.
 * @returns {number}
 */
const getZoomFromElems = (elems, fromBlock) => {
  let zoomValue = 1;
  if (typeof elems === 'object' && elems.current) {
    elems.current.forEach(elem => {
      if (elem.contains(fromBlock)) {
        zoomValue *= getElementZoom(elem);
      }
    });
  } else {
    zoomValue = grabParentsZoom(fromBlock);
  }
  return zoomValue;
};

/**
 * Функция для получения значения zoom для контейнера, в который рендерится элемент через портал.
 * Например, у компонента Popover есть элемент, по клику на который открывается PopoverBlock.
 * Этот PopoverBlock может быть расположен совершенно в другом месте dom-дерева, где значение zoom
 * будет отличаться. Значит его размеры и позиция не будут соответствовать элементу, по которому кликнули.
 *
 * В таком случае, можем воспользоваться этой функцией. Она принимает параметры zoom первым аргументом,
 * и объект с элементами вторым, где targetElement — элемент на который кликнули, а destElement — куда
 * рендерим PopoverBlock.
 *
 * Функция вернет значение zoom для контейнера, и скажет, нужно ли применить его к контейнеру,
 * или к самому PopoverBlock.
 *
 * @param {ZoomParams} zoom Ref с Map или Set элементов на которых установлен зум, или boolean для автоматического вычисления.
 * @param {{ targetElement?: Element | null | undefined; destElement?: Element | null | undefined; }} elems Элементы targetElement и destElement.
 * @returns {{ zoomValue: number; applyZoomToContainer: boolean; }} Флаг applyZoomToContainer показывает, применять ли zoom к контейнеру PopoverBlock, или к самому PopoverBlock.
 */
export const getContainerZoom = (zoom, elems) => {
  const {
    targetElement,
    destElement
  } = elems;
  let zoomValue = 1;
  if (!targetElement) {
    return {
      zoomValue
    };
  }
  if (typeof zoom === 'object') {
    const blockZoom = getZoomFromElems(zoom, targetElement);
    const containerZoom = destElement ? getZoomFromElems(zoom, destElement) : 1;
    zoomValue = blockZoom / containerZoom;
  } else if (zoom && typeof zoom === 'boolean') {
    zoomValue = grabParentsZoom(targetElement) / (destElement ? grabParentsZoom(destElement) : 1);
  }
  return {
    zoomValue: Number(zoomValue.toFixed(3)),
    applyZoomToContainer: destElement ? !isRootElement(destElement) : false
  };
};
export const ZoomContext = /*#__PURE__*/createContext({
  zoom: false
});
export const useZoomContext = () => {
  return useContext(ZoomContext);
};
export const useZoom = params => {
  const {
    zoom
  } = useZoomContext();
  if (!zoom || !isBrowser) {
    return null;
  }

  /**
   * Если на html-теге не установлено значение zoom, то на ретине
   * window.getComputedStyle(document.documentElement).zoom вернет 2.
   * https://bugs.chromium.org/p/chromium/issues/detail?id=899707#c12
   * Аналогично, если установлен зум в браузере,
   * то window.getComputedStyle(document.documentElement).zoom вернет
   * значение зума браузера, а значит не получится определить,
   * от браузера пришло значение, или из стилей.
   * Оба кейса решаются, если на html будет установлено значение zoom.
   */
  const initialZoomStyle = document.querySelector(`[${ZOOM_ATTR_NAME}]`);
  if (document.head && !initialZoomStyle) {
    const style = document.createElement('style');
    style.setAttribute(ZOOM_ATTR_NAME, 'true');
    style.appendChild(document.createTextNode('html { zoom: 1 }'));
    document.head.prepend(style);
  }
  const htmlZoom = getElementZoom(document.documentElement);
  // eslint-disable-next-line compat/compat
  const bodyZoom = getElementZoom(document.body);
  const {
    zoomValue,
    applyZoomToContainer
  } = getContainerZoom(zoom, params);
  return {
    htmlZoom,
    bodyZoom,
    zoomValue,
    applyZoomToContainer
  };
};